import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createFacility,
  deleteFacilityById,
  getClinicFacilitiesWoLoader,
  updateFacilityById,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { isActiveRecord } from "../../../util/activeStatus";
import { delBtn, w20h20BlueIcon } from "../../../util/CommonIconStyle";
import { facilityTypes, locationCategoryList } from "../../../util/dropList";
import { patchMandateSession } from "../../../util/mandateSessionStorage";

const inventoryAccessTypes = {
  ALL: "allPermittedStorage",
  BY_CATEGORY: "byCategory",
  SELECTED: "selectedLocations",
};

const requiredFieldList = ["facilityType", "name", "inventoryAccess"];

const buildAccessValue = (
  inventoryAccess,
  categories,
  locationIds,
  locations,
  locationCategoryOptions,
) => {
  if (inventoryAccess === inventoryAccessTypes.BY_CATEGORY) {
    return (categories ?? []).map((selectedValue) => {
      const option = locationCategoryOptions?.find(
        (li) => li?.value === selectedValue,
      );
      return {
        id: option?.id ?? null,
        name: option?.name ?? String(selectedValue),
      };
    });
  }
  if (inventoryAccess === inventoryAccessTypes.SELECTED) {
    return (locationIds ?? []).map((id) => {
      const loc = locations?.find((li) => li?.id === id);
      return { id, name: loc?.locationName ?? "" };
    });
  }
  return [];
};

const parseFacilityAccess = (facility) => {
  const inventoryAccess = facility?.inventoryAccess ?? inventoryAccessTypes.ALL;
  const accessValue = Array.isArray(facility?.accessValue)
    ? facility.accessValue
    : [];

  if (inventoryAccess === inventoryAccessTypes.BY_CATEGORY) {
    return {
      inventoryAccess,
      accessCategories: accessValue
        .map((item) => item?.id ?? item?.name)
        .filter((value) => value != null && value !== ""),
      accessLocationIds: [],
    };
  }
  if (inventoryAccess === inventoryAccessTypes.SELECTED) {
    return {
      inventoryAccess,
      accessCategories: [],
      accessLocationIds: accessValue.map((item) => item?.id).filter(Boolean),
    };
  }
  return {
    inventoryAccess,
    accessCategories: [],
    accessLocationIds: [],
  };
};
const tblHdrs = [
  "sNo",
  "name",
  "facilityType",
  "newStatus",
  "createdBy",
  "createdDate",
  "actions",
];

const nameExpan = {
  ConsultationRoom: "Consultation Room",
  OperationTheatre: "Operation Theatre",
  Kennel: "Kennel",
  Devices: "Devices",
  BathBays: "Bath Bays",
  HomeKit: "Home Kit",
  TreatmentArea: "Treatment Area",
  Others: "Others",
};

const facilityTypeFilterList = [
  { name: "All", value: "all" },
  ...facilityTypes,
];

const toFacilityList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const Facilities = () => {
  const dispatch = useDispatch();
  const locationsAndFacilitiesFetchedRef = useRef(false);
  const isFirstFilterRenderRef = useRef(true);
  const [searchValue, setSearchValue] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [modVisible, setModVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modValues, setModValues] = useState({});
  const [modErrors, setModErrors] = useState({});
  const [delModVisible, setDelModVisible] = useState(false);
  const [page, setPage] = useState(1);
  const { facilities } = useSelector((state) => state?.clinic);
  const itemSize = 10;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { locations, locationCategoryOptions } = useMemo(() => {
    const defaultCategoryOptions = locationCategoryList.map((li) => ({
      name: li.name,
      value: li.value,
      id: null,
    }));

    if (!mandateDataRaw) {
      return {
        locations: [],
        locationCategoryOptions: defaultCategoryOptions,
      };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const categories = Array.isArray(reqData?.categories)
        ? reqData.categories
        : [];
      const locationCategories = categories?.filter(
        (loc) => loc?.type === "location" && loc?.name,
      );
      const fromMandate = locationCategories
        ?.filter((loc) => isActiveRecord(loc))
        ?.map((loc) => ({
          name: loc.name,
          value: loc.id ?? loc.name,
          id: loc.id ?? null,
        }));
      const inactiveNames = new Set(
        locationCategories
          ?.filter((loc) => !isActiveRecord(loc))
          ?.map((loc) => loc?.name?.toLowerCase())
          ?.filter(Boolean),
      );
      const mandateNames = new Set(
        fromMandate.map((li) => li?.name?.toLowerCase()),
      );
      const merged = [
        ...fromMandate,
        ...defaultCategoryOptions.filter(
          (li) =>
            !mandateNames.has(li?.name?.toLowerCase()) &&
            !inactiveNames.has(li?.name?.toLowerCase()),
        ),
      ];
      return {
        locations: (Array.isArray(reqData?.locations) ? reqData?.locations : [])
          ?.filter((loc) => isActiveRecord(loc)),
        locationCategoryOptions: merged,
      };
    } catch {
      return {
        locations: [],
        locationCategoryOptions: defaultCategoryOptions,
      };
    }
  }, [mandateDataRaw]);

  const filteredFacilities = useMemo(() => {
    let list = Array.isArray(facilities) ? facilities : [];
    if (filterType !== "all") {
      list = list.filter((facility) => facility?.type === filterType);
    }
    if (searchValue?.length > 0) {
      const search = searchValue.toLowerCase();
      list = list.filter((facility) =>
        facility?.name?.toLowerCase()?.includes(search),
      );
    }
    return list;
  }, [facilities, filterType, searchValue]);

  const paginatedFacilities = useMemo(() => {
    const start = (page - 1) * itemSize;
    return filteredFacilities.slice(start, start + itemSize);
  }, [filteredFacilities, page, itemSize]);

  useEffect(() => {
    if (locationsAndFacilitiesFetchedRef.current) return;
    locationsAndFacilitiesFetchedRef.current = true;
    onFacilitiesApiCall(1);
  }, [dispatch]);

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    setPage(1);
  }, [filterType, searchValue]);

  const onFacilitiesApiCall = (pge = 1, patchMandate = false) => {
    try {
      setPage(pge);
      dispatch(getClinicFacilitiesWoLoader()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          if (!patchMandate) return;
          patchMandateSession({
            facilities: toFacilityList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore facilities refresh errors
    }
  };

  const getLocationList = () => {
    const reqList =
      locations?.length > 0
        ? locations
            ?.filter((li) => li?.locationType?.toLowerCase() === "storage")
            ?.map((li) => ({
              name: li?.locationName,
              value: li?.id,
            }))
        : [];
    return reqList;
  };

  const onCreate = () => {
    setSelectedItem(null);
    setModValues({ inventoryAccess: inventoryAccessTypes.ALL });
    setModVisible(true);
  };

  const onCloseModal = () => {
    setModVisible(false);
    setModValues({});
    setModErrors({});
  };

  const onChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = { ...modValues, [name]: value };
    if (
      name === "facilityType" &&
      !["Devices", "Others"].includes(value) &&
      !reqObj.inventoryAccess
    ) {
      reqObj.inventoryAccess = inventoryAccessTypes.ALL;
    }
    setModValues(reqObj);
    setModErrors({
      ...modErrors,
      [name]: !value || value?.length === 0 ? "This field is required" : "",
    });
  };

  const onInventoryAccessChange = (e) => {
    const value = e?.target?.value;
    setModValues({
      ...modValues,
      inventoryAccess: value,
      accessCategories: [],
      accessLocationIds: [],
    });
    setModErrors({
      ...modErrors,
      inventoryAccess: "",
      accessCategories: "",
      accessLocationIds: "",
    });
  };

  const onMultiSelectChange = (e, name) => {
    const value = e?.target?.value ?? [];
    const accessType = modValues?.inventoryAccess;
    const isCategoryField =
      name === "accessCategories" &&
      accessType === inventoryAccessTypes.BY_CATEGORY;
    const isLocationField =
      name === "accessLocationIds" &&
      accessType === inventoryAccessTypes.SELECTED;

    setModValues({ ...modValues, [name]: value });

    if (!isCategoryField && !isLocationField) return;

    setModErrors({
      ...modErrors,
      [name]:
        value?.length === 0
          ? name === "accessCategories"
            ? "Please select at least one category"
            : "Please select at least one location"
          : "",
    });
  };

  const onFilterTypeChange = (e) => {
    setFilterType(e?.target?.value);
    setPage(1);
  };

  const isFieldsNotValid = () => {
    const isDevicesOrOthers = ["Devices", "Others"].includes(
      modValues?.facilityType,
    );
    const errObj = {
      facilityType: "",
      name: "",
      inventoryAccess: "",
      accessCategories: "",
      accessLocationIds: "",
    };

    requiredFieldList.forEach((fld) => {
      if (!modValues?.[fld] || modValues?.[fld]?.length === 0) {
        errObj[fld] =
          fld === "inventoryAccess" && isDevicesOrOthers
            ? ""
            : "This field is required";
      }
    });

    if (!isDevicesOrOthers) {
      if (
        modValues?.inventoryAccess === inventoryAccessTypes.BY_CATEGORY &&
        (!modValues?.accessCategories ||
          modValues?.accessCategories?.length === 0)
      ) {
        errObj.accessCategories = "Please select at least one category";
      }
      if (
        modValues?.inventoryAccess === inventoryAccessTypes.SELECTED &&
        (!modValues?.accessLocationIds ||
          modValues?.accessLocationIds?.length === 0)
      ) {
        errObj.accessLocationIds = "Please select at least one location";
      }
    }

    setModErrors(errObj);
    return Object.values(errObj).some((err) => err);
  };

  const onSubmit = () => {
    if (isFieldsNotValid()) return;
    const data = {
      type: modValues?.facilityType,
      name: modValues?.name,
      status: selectedItem?.status ?? true,
    };
    if (
      !["Devices", "Others"].includes(modValues?.facilityType) &&
      modValues?.inventoryAccess
    ) {
      data.inventoryAccess = modValues.inventoryAccess;
      data.accessValue = buildAccessValue(
        modValues.inventoryAccess,
        modValues.accessCategories,
        modValues.accessLocationIds,
        locations,
        locationCategoryOptions,
      );
    }
    if (!selectedItem) {
      dispatch(createFacility(data)).then((res) => {
        if (res?.payload) {
          onCloseModal();
          onFacilitiesApiCall(page, true);
        }
      });
    } else {
      dispatch(
        updateFacilityById({ data, facilityId: selectedItem?.facilityId }),
      ).then((res) => {
        if (res?.payload) {
          onCloseModal();
          onFacilitiesApiCall(page, true);
        }
      });
    }
  };

  const handleDelete = (facility) => {
    setSelectedItem(facility);
    setDelModVisible(true);
  };

  const handleEdit = (facility) => {
    const { inventoryAccess, accessCategories, accessLocationIds } =
      parseFacilityAccess(facility);
    setModValues({
      facilityType: facility?.type,
      name: facility?.name,
      inventoryAccess,
      accessCategories,
      accessLocationIds,
    });
    setSelectedItem(facility);
    setModVisible(true);
  };

  const handleChangeSwitch = (status, facility) => {
    const data = {
      type: facility?.type,
      name: facility?.name,
      status,
    };
    if (facility?.inventoryAccess) {
      data.inventoryAccess = facility.inventoryAccess;
      data.accessValue = Array.isArray(facility?.accessValue)
        ? facility.accessValue
        : [];
    }
    dispatch(
      updateFacilityById({ data, facilityId: facility?.facilityId }),
    ).then((res) => {
      if (res?.payload) {
        onFacilitiesApiCall(page, true);
      }
    });
  };

  const getTableData = () => {
    return paginatedFacilities?.map((row, index) => ({
      sNo: (page - 1) * itemSize + index + 1,
      name: row?.name,
      facilityType: nameExpan?.[row?.type],
      newStatus: (
        <div>
          <div>
            <CustomSwitch
              value={row?.status}
              onChange={(e) => handleChangeSwitch(e?.target?.value, row)}
              greenToRed
            />
          </div>
          <div>{row?.status ? "Active" : "Under Maintenance"}</div>
        </div>
      ),
      createdBy: row?.userName || "-",
      createdDate: new Date(row?.createdDate).toLocaleDateString(),
      actions: (
        <div className="flex-row-ali-cen">
          <div className="cursor" onClick={() => handleEdit(row)}>
            <div className="flex-center">
              <EditIcon sx={w20h20BlueIcon} />
            </div>
            <Typography className="blue-color fs8 txt-semi-bold text-align-center">
              Edit
            </Typography>
          </div>
          <div className="cursor ml15" onClick={() => handleDelete(row)}>
            <div className="flex-center">
              <DeleteOutlineIcon sx={delBtn} />
            </div>
            <Typography className="red3 fs8 txt-semi-bold text-align-center">
              Delete
            </Typography>
          </div>
        </div>
      ),
    }));
  };

  const onCloseDelMod = () => {
    setDelModVisible(false);
    setSelectedItem(null);
  };

  const onConfirmDelete = () => {
    dispatch(deleteFacilityById(selectedItem?.facilityId)).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        onFacilitiesApiCall(1, true);
      }
    });
  };

  const handleChangePage = (e, pge) => {
    setPage(pge);
  };

  return (
    <>
      <div className="flex-row mt20">
        <div className="w30Per" />
        <div className="w40Per">
          <div className="flex-row-ali-cen flex-center">
            <CustomTextField
              placeholder="Search"
              fullWidth
              name="search"
              handleChange={(e) => setSearchValue(e?.target?.value)}
              value={searchValue}
              backgroundColor={AppColors.lightBlue2}
            />
            <div className="ml20">
              <CustomButton text="Create" onClick={onCreate} smallBtn />
            </div>
          </div>
        </div>
        <div className="w30Per">
          <div className="flex1-end">
            <div className="w60Per mr30">
              <CustomSelect
                list={facilityTypeFilterList}
                value={filterType}
                name="facilityType"
                label="Facility Type"
                handleChange={onFilterTypeChange}
                select
                backgroundColor={AppColors.lightBlue2}
              />
            </div>
          </div>
        </div>
      </div>

      <CustomTable
        grey
        columns={tblHdrs}
        datas={getTableData()}
        totalRecords={filteredFacilities?.length}
        rowsPerPage={itemSize}
        page={page}
        handleChangePage={handleChangePage}
        maxHeight="70vh"
      />

      <CustomModal
        rightModal
        open={modVisible}
        onClose={onCloseModal}
        modalWidth={30}
        header={selectedItem ? "Edit" : "Create"}
      >
        <div className="scroll-80vh">
          <Grid container spacing={2} className="ph20 mt5">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <CustomSelect
                list={facilityTypes}
                value={modValues?.facilityType}
                name="facilityType"
                error={!!modErrors?.facilityType}
                label="Facility Type *"
                helperText={modErrors?.facilityType}
                handleChange={onChange}
                select
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <CustomTextField
                value={modValues?.name}
                name="name"
                error={!!modErrors?.name}
                label={`Name ${!["Devices", "Others"].includes(modValues?.facilityType) ? "*" : ""}`}
                helperText={modErrors?.name}
                handleChange={(e) => {
                  const val = e?.target?.value?.replace(/[^a-zA-Z0-9 ]/g, "");
                  onChange({ target: { name: "name", value: val } });
                }}
                fullWidth
              />
            </Grid>
            {!["Devices", "Others"].includes(modValues?.facilityType) ? (
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                className="mt10"
              >
                <FormControl
                  component="fieldset"
                  error={!!modErrors?.inventoryAccess}
                  fullWidth
                >
                  <Typography className="txt-semi-bold fs14 light-grey mb5">
                    Inventory Access *
                  </Typography>
                  <RadioGroup
                    value={
                      modValues?.inventoryAccess ?? inventoryAccessTypes.ALL
                    }
                    onChange={onInventoryAccessChange}
                  >
                    <div className="flex-row-ali-cen">
                      <FormControlLabel
                        value={inventoryAccessTypes.ALL}
                        control={<Radio />}
                        label="All permitted storage"
                      />
                      <FormControlLabel
                        value={inventoryAccessTypes.BY_CATEGORY}
                        control={<Radio />}
                        label="By Category"
                        className="ml20"
                      />
                    </div>
                    <FormControlLabel
                      value={inventoryAccessTypes.SELECTED}
                      control={<Radio />}
                      label="Selected Locations"
                    />
                  </RadioGroup>
                  {modErrors?.inventoryAccess ? (
                    <Typography className="red-color fs12 mt5">
                      {modErrors.inventoryAccess}
                    </Typography>
                  ) : null}
                </FormControl>
                {modValues?.inventoryAccess ===
                inventoryAccessTypes.BY_CATEGORY ? (
                  <div className="mt10">
                    <CustomSelect
                      list={locationCategoryOptions}
                      value={modValues?.accessCategories ?? []}
                      name="accessCategories"
                      error={!!modErrors?.accessCategories}
                      label="Category *"
                      helperText={modErrors?.accessCategories}
                      handleChange={(e) =>
                        onMultiSelectChange(e, "accessCategories")
                      }
                      multiSelectTagCheck
                    />
                  </div>
                ) : null}
                {modValues?.inventoryAccess ===
                inventoryAccessTypes.SELECTED ? (
                  <div className="mt10">
                    <CustomSelect
                      list={getLocationList()}
                      value={modValues?.accessLocationIds ?? []}
                      name="accessLocationIds"
                      error={!!modErrors?.accessLocationIds}
                      label="Locations *"
                      helperText={modErrors?.accessLocationIds}
                      handleChange={(e) =>
                        onMultiSelectChange(e, "accessLocationIds")
                      }
                      multiSelectTagCheck
                    />
                  </div>
                ) : null}
              </Grid>
            ) : null}

            <div className="flex1-center mt20">
              <div>
                <CustomButton text="Submit" onClick={onSubmit} smallBtn />
              </div>
            </div>
          </Grid>
        </div>
      </CustomModal>

      <CustomModal
        open={delModVisible}
        onClose={onCloseDelMod}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the facility?
        </Typography>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="flex-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                smallBtn
                onClick={onConfirmDelete}
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </>
  );
};

export default Facilities;
